import React, { useState, useEffect } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material'
import { apiService } from '@/services/api'

interface Centre {
  id: string
  name: string
  code: string
}

interface AdminUser {
  id: string
  email: string
  phone: string
  first_name: string
  last_name: string
  role: string
  status: string
  centre_id: string | null
  centre_name: string | null
  centre_code: string | null
  email_verified: boolean
  phone_verified: boolean
  created_at: string
  last_login: string | null
}

const AdminsTab: React.FC = () => {
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [centres, setCentres] = useState<Centre[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null)
  const [centreDialog, setCentreDialog] = useState(false)
  const [statusDialog, setStatusDialog] = useState(false)
  const [selectedCentre, setSelectedCentre] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<'active' | 'inactive'>('active')

  useEffect(() => {
    fetchAdmins()
    fetchCentres()
  }, [])

  const fetchAdmins = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await apiService.getAllAdmins()
      setAdmins(Array.isArray(response.data) ? response.data : [])
    } catch (err: any) {
      setError(err.message || 'Failed to fetch admin users')
    } finally {
      setLoading(false)
    }
  }

  const fetchCentres = async () => {
    try {
      const response = await apiService.getCentres()
      setCentres(Array.isArray(response.data) ? response.data : [])
    } catch (err: any) {
      console.error('Failed to fetch centres:', err)
    }
  }

  const handleChangeCentre = (admin: AdminUser) => {
    setSelectedAdmin(admin)
    setSelectedCentre(admin.centre_id || '')
    setCentreDialog(true)
  }

  const handleChangeStatus = (admin: AdminUser) => {
    setSelectedAdmin(admin)
    setSelectedStatus(admin.status === 'active' ? 'inactive' : 'active')
    setStatusDialog(true)
  }

  const confirmChangeCentre = async () => {
    if (!selectedAdmin || !selectedCentre) return

    try {
      await apiService.updateAdminCentre(selectedAdmin.id, selectedCentre)
      setSuccessMessage('Admin centre assignment updated successfully')
      setCentreDialog(false)
      fetchAdmins()
    } catch (err: any) {
      setError(err.message || 'Failed to update admin centre')
    }
  }

  const confirmChangeStatus = async () => {
    if (!selectedAdmin) return

    try {
      await apiService.updateAdminStatus(selectedAdmin.id, selectedStatus)
      setSuccessMessage(`Admin ${selectedStatus === 'active' ? 'activated' : 'deactivated'} successfully`)
      setStatusDialog(false)
      fetchAdmins()
    } catch (err: any) {
      setError(err.message || 'Failed to update admin status')
    }
  }

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleString()
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage all admin users across all centres. You can assign or change their centre and activate/deactivate their accounts.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Centre</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Verified</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>{`${admin.first_name} ${admin.last_name}`}</TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>{admin.phone}</TableCell>
                  <TableCell>
                    {admin.centre_name ? (
                      <Chip 
                        label={`${admin.centre_code} - ${admin.centre_name}`} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="No Centre" size="small" color="warning" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={admin.status} 
                      size="small" 
                      color={admin.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {admin.email_verified && admin.phone_verified ? '✅' : '❌'}
                  </TableCell>
                  <TableCell>{formatDateTime(admin.last_login)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleChangeCentre(admin)}
                      >
                        Change Centre
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color={admin.status === 'active' ? 'error' : 'success'}
                        onClick={() => handleChangeStatus(admin)}
                      >
                        {admin.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {admins.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No admin users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Change Centre Dialog */}
      <Dialog open={centreDialog} onClose={() => setCentreDialog(false)}>
        <DialogTitle>Change Admin Centre</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign {selectedAdmin?.first_name} {selectedAdmin?.last_name} to a different centre
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Centre</InputLabel>
            <Select
              value={selectedCentre}
              onChange={(e) => setSelectedCentre(e.target.value)}
              label="Centre"
            >
              {centres.map((centre) => (
                <MenuItem key={centre.id} value={centre.id}>
                  {centre.code} - {centre.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCentreDialog(false)}>Cancel</Button>
          <Button 
            onClick={confirmChangeCentre} 
            variant="contained" 
            disabled={!selectedCentre}
          >
            Update Centre
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Status Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>
          {selectedStatus === 'active' ? 'Activate' : 'Deactivate'} Admin User
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {selectedStatus === 'active' ? 'activate' : 'deactivate'}{' '}
            {selectedAdmin?.first_name} {selectedAdmin?.last_name}?
          </Typography>
          {selectedStatus === 'inactive' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This admin will not be able to log in or access the system until reactivated.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button 
            onClick={confirmChangeStatus} 
            variant="contained"
            color={selectedStatus === 'active' ? 'success' : 'error'}
          >
            {selectedStatus === 'active' ? 'Activate' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AdminsTab
